import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import ExplanationViewer from '@/components/studies/ExplanationViewer';
import ChallengeResponsesPanel from '@/components/classroom/ChallengeResponsesPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  playRewardSound,
  playCelebrationSound,
} from '@/lib/explanationSounds';

function CelebrationConfetti() {
  const pieces = Array.from({ length: 28 }, (_, index) => ({
    id: index,
    left: `${8 + ((index * 31) % 84)}%`,
    rotate: `${(index * 47) % 360}deg`,
    delay: (index % 7) * 0.06,
    duration: 1.6 + (index % 5) * 0.18,
    x: ((index % 2 === 0 ? 1 : -1) * (20 + (index % 6) * 9)),
  }));

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[100] overflow-hidden"
      aria-hidden="true"
    >
      {pieces.map((piece) => (
        <motion.div
          key={piece.id}
          className="absolute top-[12%] h-3 w-2 rounded-sm bg-emerald-500"
          style={{
            left: piece.left,
            rotate: piece.rotate,
          }}
          initial={{
            y: -20,
            x: 0,
            opacity: 0,
            scale: 0.7,
          }}
          animate={{
            y: '82vh',
            x: piece.x,
            opacity: [0, 1, 1, 0],
            scale: [0.7, 1, 1, 0.85],
            rotate: [0, 120, 240, 360],
          }}
          transition={{
            duration: piece.duration,
            delay: piece.delay,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// C1 · Wrapper EXCLUSIVO de Classroom Live para presentar una Explanation
// dirigida por el profesor. REUTILIZA `ExplanationViewer` SIN MODIFICARLO y no
// duplica su lógica (explain / explore / challenge square / challenge move /
// legacy siguen viviendo íntegramente en el Viewer).
//
// Responsabilidad única: mostrar el Step correspondiente a
// `active_explanation.current_step` (fuente de verdad = servidor).
//
// C3-1 · Cuando el alumno (canRespond) resuelve un challenge, el Viewer nos
// notifica el resultado ya calculado localmente vía `onChallengeResult`.
// Aquí solo lo persistimos en el Classroom (POST) y mostramos una confirmación
// visual breve al alumno. NO avanzamos de Step (el profesor controla el ritmo).
export default function ClassroomExplanationView({
  activeExplanation,
  roomId,
  canRespond = false,
  challengeIntroVisible = false,
  isTeacher = false,
  teacherPanelRoom = null,
  teacherNavigation = null,
}) {
  const stepActivity = useMemo(() => {
    const ae = activeExplanation;
    if (!ae || !ae.activity) return null;
    const a = ae.activity;
    const steps = Array.isArray(a.steps) ? a.steps : null;
    if (steps && steps.length > 0) {
      const idx = Math.max(0, Math.min(Number(ae.current_step) || 0, steps.length - 1));
      return { ...a, id: `${ae.id}-step-${idx}`, steps: [steps[idx]] };
    }
    // Legacy (sin steps) → el Viewer reconstruye 1 step virtual desde
    // title/fen/pgn/text. current_step siempre es 0 (total_steps === 1).
    return { ...a, id: `${ae.id}-legacy` };
  }, [activeExplanation]);

  // Devuelve true cuando el step actual es el último ejercicio interactivo
  // de toda la Explanation Activity. Los steps `explain` no cuentan como
  // ejercicios completables.
  const isLastInteractiveExercise = useMemo(() => {
    const ae = activeExplanation;
    const steps = Array.isArray(ae?.activity?.steps) ? ae.activity.steps : [];
    const currentStep = Number(ae?.current_step) || 0;

    const interactiveIndexes = steps
      .map((step, index) => ({
        mode: step?.mode,
        index,
      }))
      .filter(({ mode }) => mode === 'challenge' || mode === 'explore')
      .map(({ index }) => index);

    if (interactiveIndexes.length === 0) return false;

    return interactiveIndexes[interactiveIndexes.length - 1] === currentStep;
  }, [activeExplanation]);

  // La celebración se muestra una sola vez por Step; se reinicia al cambiar de
  // Step oficial (id distinto por step) o al reabrir.
  const celebratedRef = useRef(false);
  const exploreFinishCelebratedRef = useRef(false);
  const [showFinishCelebration, setShowFinishCelebration] = React.useState(false);

  useEffect(() => {
    celebratedRef.current = false;
    exploreFinishCelebratedRef.current = false;
    setShowFinishCelebration(false);
  }, [stepActivity?.id]);

  const handleExploreComplete = useCallback(() => {
    if (!canRespond || !activeExplanation) return;

    if (isLastInteractiveExercise) {
      if (!exploreFinishCelebratedRef.current) {
        exploreFinishCelebratedRef.current = true;
        playCelebrationSound();
        setShowFinishCelebration(true);
      }
      return;
    }

    playRewardSound();
  }, [canRespond, activeExplanation, isLastInteractiveExercise]);

  const handleExploreProgress = useCallback((completed) => {
    if (!canRespond || !roomId || !activeExplanation) return;

    api.post(`/classrooms/${roomId}/explanation/explore-progress`, {
      completed,
      current_step: Number(activeExplanation.current_step) || 0,
    }).catch(() => {});
  }, [canRespond, roomId, activeExplanation?.current_step, activeExplanation?.id]);

  const handleChallengeResult = useCallback((status) => {
    if (!canRespond || !roomId) return;

    // Persistir el resultado (el backend deriva step_id + user_id; 'correct'
    // nunca se degrada). Fire-and-forget: no bloquea el tablero.
    api.post(`/classrooms/${roomId}/explanation/response`, { status }).catch(() => {});

    if (status === 'correct' && !celebratedRef.current) {
      celebratedRef.current = true;

      if (isLastInteractiveExercise) {
        playCelebrationSound();
        setShowFinishCelebration(true);
        return;
      }

      playRewardSound();

      toast.success('🎉 ¡Lo conseguiste!', {
        description: '¡Muy bien! Continuamos con la clase.',
        duration: 3500,
      });
    }
  }, [canRespond, roomId, isLastInteractiveExercise]);

  if (!stepActivity) return null;
  // `open` permanece true mientras exista active_explanation. `onClose` inerte:
  // el alumno no puede descartar la clase — el ciclo de vida (finalizar) lo
  // controla el profesor vía el endpoint stop (C2).
  return (
    <>
      <ExplanationViewer
        open
        activity={stepActivity}
        onClose={() => {}}
        onChallengeResult={canRespond ? handleChallengeResult : undefined}
        onExploreComplete={canRespond ? handleExploreComplete : undefined}
        onExploreProgress={canRespond ? handleExploreProgress : undefined}
        showChrome={false}
        teacherPanel={
          isTeacher && teacherPanelRoom &&
          ['challenge', 'explore'].includes(stepActivity?.steps?.[0]?.mode)
            ? <ChallengeResponsesPanel room={teacherPanelRoom} inline />
            : undefined
        }
        teacherNavigation={isTeacher ? teacherNavigation : undefined}
        challengeIntroVisible={challengeIntroVisible}
        liveProgress={canRespond ? {
          currentStep: activeExplanation?.current_step,
          totalSteps: activeExplanation?.total_steps,
        } : null}
      />

      {canRespond && (
        <AnimatePresence>
          {showFinishCelebration && <CelebrationConfetti />}
        </AnimatePresence>
      )}

      {canRespond && (
        <Dialog
          open={showFinishCelebration}
          onOpenChange={setShowFinishCelebration}
        >
          <DialogContent
            data-testid="student-explanation-finish-dialog"
            className="sm:max-w-md text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
            >
              <DialogHeader className="items-center">
                <motion.div
                  className="text-6xl leading-none mb-2"
                  aria-hidden="true"
                  initial={{ scale: 0.5, rotate: -12 }}
                  animate={{
                    scale: [0.5, 1.15, 1],
                    rotate: [-12, 8, 0],
                  }}
                  transition={{
                    duration: 0.55,
                    ease: 'easeOut',
                  }}
                >
                  🎉
                </motion.div>

                <DialogTitle className="text-3xl font-extrabold">
                  ¡Felicidades!
                </DialogTitle>

                <DialogDescription className="space-y-2 pt-2 text-base">
                  <span className="block text-xl font-bold text-emerald-600">
                    Prueba superada
                  </span>
                  <span className="block">
                    Has completado este tema.
                  </span>
                  <span className="block font-semibold">
                    ¡Vamos al siguiente tema!
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="flex justify-center pt-2">
                <Button
                  data-testid="student-explanation-finish-close"
                  onClick={() => setShowFinishCelebration(false)}
                  className="rounded-xl px-6"
                >
                  ¡Entendido!
                </Button>
              </div>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
